import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export function HoldingBankTab({ holding, reload }) {
  const [form, setForm] = useState({
    bank_name: holding.bank_name || "",
    account_name: holding.account_name || "",
    routing_number: holding.routing_number || "",
    account_number: holding.account_number || "",
    swift: holding.swift || "",
    memo_note: holding.memo_note || "",
  });
  const [busy, setBusy] = useState(false);
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    api.get("/admin/holding-bank/ledger").then((r) => setLedger(r.data)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hasBank = !!(form.bank_name && form.account_number);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/holding-bank", form);
      toast.success("Holding bank saved");
      reload();
      const l = await api.get("/admin/holding-bank/ledger");
      setLedger(l.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const masked = form.account_number ? `•••• ${form.account_number.slice(-4)}` : "—";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8" data-testid="holding-panel">
      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-8 space-y-6">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Custody</p>
          <h3 className="serif text-3xl mt-1">Holding bank account</h3>
          <p className="text-sm text-[#4A5C50] mt-1">
            Destination bank where customer funds are held when a card is credited via internal Load funds. Receivers see a masked version
            ("•••• last 4") on the Load funds confirmation for transparency.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <BankField label="Bank name" value={form.bank_name} onChange={set("bank_name")} placeholder="e.g. First Republic Bank" testId="holding-bank-name" />
          <BankField label="Account holder" value={form.account_name} onChange={set("account_name")} placeholder="Zcardon Holding LLC" testId="holding-account-name" />
          <BankField label="Routing number" value={form.routing_number} onChange={set("routing_number")} placeholder="9-digit routing" testId="holding-routing" mono />
          <BankField label="Account number" value={form.account_number} onChange={set("account_number")} placeholder="4–17 digits" testId="holding-account" mono />
          <BankField label="SWIFT / BIC (optional)" value={form.swift} onChange={set("swift")} placeholder="e.g. CHASUS33" testId="holding-swift" mono />
          <BankField label="Memo note" value={form.memo_note} onChange={set("memo_note")} placeholder="Shown to customers" testId="holding-memo" />
        </div>

        <Button onClick={save} disabled={busy}
          className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
          data-testid="holding-save-btn">
          {busy ? "Saving…" : "Save holding bank"}
        </Button>
      </div>

      <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-8 space-y-4" data-testid="holding-preview">
        <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Customer sees</p>
        {hasBank ? (
          <>
            <div className="pt-2">
              <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Funds are held at</p>
              <p className="serif text-2xl mt-1">{form.bank_name || "—"}</p>
              {form.account_name && <p className="text-sm opacity-70 mt-1">{form.account_name}</p>}
            </div>
            <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="opacity-60">Account</span><span className="mono">{masked}</span></div>
              {form.routing_number && <div className="flex justify-between"><span className="opacity-60">Routing</span><span className="mono">{form.routing_number}</span></div>}
              {form.swift && <div className="flex justify-between"><span className="opacity-60">SWIFT</span><span className="mono">{form.swift}</span></div>}
            </div>
            {form.memo_note && (
              <p className="text-[11px] opacity-60 pt-3">{form.memo_note}</p>
            )}
          </>
        ) : (
          <p className="text-sm opacity-70">Add a bank name and account number to expose a custody summary to customers on every Load Funds confirmation.</p>
        )}
      </div>

      {ledger && (
        <div className="lg:col-span-2 space-y-4" data-testid="holding-ledger">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <SummaryCard label="Live custody (owed to cardholders)" value={`$${ledger.live_balance.toFixed(2)}`} accent="primary" testId="holding-live-balance" />
            <SummaryCard label="Loaded via Load Funds (lifetime)" value={`$${ledger.total_loaded.toFixed(2)}`} testId="holding-total-loaded" />
            <SummaryCard label="Load Fund entries" value={`${ledger.entry_count}`} testId="holding-entry-count" />
          </div>
          <div className="bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E5E0D8] flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Recent holding bank credits</p>
                <p className="serif text-lg">Last {ledger.entries.length} entries</p>
              </div>
              <LedgerExport />
            </div>
            <table className="w-full text-sm">
              <thead className="text-left text-[#4A5C50] text-xs uppercase tracking-widest">
                <tr>
                  <th className="px-6 py-3">When</th>
                  <th className="px-6 py-3">Bank</th>
                  <th className="px-6 py-3">Account</th>
                  <th className="px-6 py-3 text-right">Amount</th>
                  <th className="px-6 py-3">Card</th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-6 text-[#4A5C50]">No holding-bank credits yet. Ask a customer to use Load funds.</td></tr>
                )}
                {ledger.entries.map((e) => (
                  <tr key={e.id} className="border-t border-[#E5E0D8]" data-testid={`holding-ledger-row-${e.id}`}>
                    <td className="px-6 py-3 mono text-xs">{new Date(e.ts).toLocaleString()}</td>
                    <td className="px-6 py-3">{e.holding_bank_name || "—"}</td>
                    <td className="px-6 py-3 mono text-xs">{e.holding_account_masked || "—"}</td>
                    <td className="px-6 py-3 mono text-right">${Number(e.amount_usd).toFixed(2)}</td>
                    <td className="px-6 py-3 mono text-xs text-[#4A5C50]">{(e.card_id || "").slice(0, 8)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, accent, testId }) {
  const bg = accent === "primary" ? "bg-[#184A30] text-[#FBF9F6]" : "bg-white border border-[#E5E0D8] text-[#112318]";
  return (
    <div className={`rounded-2xl p-6 ${bg}`} data-testid={testId}>
      <p className={`text-[10px] tracking-[0.3em] uppercase ${accent === "primary" ? "opacity-60" : "text-[#4A5C50]"}`}>{label}</p>
      <p className="serif text-3xl mt-2">{value}</p>
    </div>
  );
}

function LedgerExport() {
  const [since, setSince] = useState("");
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const params = since ? `?since=${encodeURIComponent(since)}` : "";
      const r = await api.get(`/admin/holding-bank/ledger.csv${params}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `holding_ledger_${since || "all"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2">
      <div>
        <label className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Since (optional)</label>
        <input type="date" value={since}
          onChange={(e) => setSince(e.target.value)}
          className="block mono text-sm bg-transparent border-b border-[#E5E0D8] focus:border-[#184A30] py-1 focus:outline-none"
          data-testid="ledger-since" />
      </div>
      <Button onClick={download} disabled={busy}
        className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
        data-testid="ledger-export-btn">
        {busy ? "Exporting…" : "Download CSV"}
      </Button>
    </div>
  );
}

function BankField({ label, value, onChange, placeholder, testId, mono }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{label}</label>
      <input value={value} onChange={onChange} placeholder={placeholder}
        className={`w-full bg-transparent border-b border-[#E5E0D8] focus:border-[#184A30] transition py-2 text-lg text-[#112318] focus:outline-none ${mono ? "mono" : ""}`}
        data-testid={testId} />
    </div>
  );
}
